import { Command } from 'cmdk';
import { useMemo, useCallback, useState, useRef, useEffect, useLayoutEffect, type KeyboardEvent } from 'react';
import type { IconName } from '@customerio/pluma-icons';
import {
  TextField,
  Box,
  ModalPrimitiveDialog,
  ModalPrimitiveOverlay,
  ModalPrimitive,
  EmptyStateDescription,
  EmptyState,
  EmptyStateTitle,
  Icon,
  Text,
  Flex,
  SkeletonShape,
  SkeletonText,
  KeyboardShortcut,
  InlineStack,
  Stack,
  type ModalPrimitiveProps,
  type PlumaManagedModalProps,
  Filters,
  FiltersActiveList,
} from '@customerio/pluma-components/react';
import { useIsAgentFullscreen } from '@/features/copilot';
import type { useCopilotSessionContext, useCopilotSidebar } from '@/features/copilot';
import { getHydraUIAPIQueryOptions, hydraUiApi, encodeSearchFilters } from '@/services/api';
import type { paths } from '@customerio/hydra-ui-api';
import { useQueries } from '@tanstack/react-query';
import { useDebouncedValue } from '@/hooks/state/use-debounced-value';
import type { useCurrentWorkspace } from '@/context/current-workspace';
import type { useThemeContext } from '@/context/theme';
import type { useSideNavState, useToggleSideNav } from '@customerio/pluma-components/react';
import * as styles from './index.css';
import { FAST_02_DURATION } from '@/utils/motion';
import type { SearchResult, TemplateType } from '@/services/auth/types';
import { useSafeNavigate } from '@/services/router/safe-navigate';
import {
  COMMAND_DIALOG_QUERY_DEFAULTS,
  COMMAND_MODE_HOTKEY,
  DEFERRED_SOURCE_FALLBACK_DELAY_MS,
  FILTER_MODE_HOTKEY,
  LABEL,
  SEARCH_KIND_FILTERS,
  TEMPLATE_TYPE_FILTER_OPTIONS,
  getCategorySearchCopy,
  isActiveCategory,
  isSearchKindFilter,
} from './constants';
import type { ActiveCategory } from './constants';
import {
  mapCustomerToSearchResult,
  buildPeopleSearchQuery,
  buildPeopleIdFilter,
  mergePeopleResults,
  scoreCustomerResult,
  getCustomerKey,
} from './utils/customer-results';
import { groupSearchResults, remapUnsetTemplates } from './utils/result-grouping';
import { buildAllApiResults, type ScoredResult } from './utils/result-scoring';
import { getResultGroupHeading, SEARCH_RESULT_KIND_METADATA } from './utils/result-metadata';
import {
  DEFERRED_SOURCE_KEYS,
  SOURCE_KEYS,
  buildActiveSources,
  buildSourceEnabledStates,
  hasAnyDeferredSource,
  type ActiveSources,
  type SourceKey,
  type SourceQueryState,
} from './utils/source-bookkeeping';
import { useStoredHydraCommands } from './use-stored-hydra-commands';
import { useSettingsCommands } from './use-settings-commands';
import { useSegmentRoutingCommands } from './use-segment-routing-commands';
import { useCampaignRoutingCommands } from './use-campaign-routing-commands';
import { useLocalSearchResults } from './use-local-search-results';
import { buildLocalCommandResultPlan, type LocalCommandResultGroup } from './local-command-result-plan';
import { navigationSearchOptions, type PermissionGuard } from './use-navigation-search';
import { docsSearchOptions } from './use-docs-search';
import { useAdAudiencesSource } from './use-ad-audiences-source';
import { useCdpOutSource } from './use-cdp-out-source';
import { useDataOutSource } from './use-data-out-source';
import { useFormsSource } from './use-forms-source';
import { useIntegrationsSource } from './use-integrations-source';
import { useObjectTypesSource } from './use-object-types-source';
import { useWebhooksSource } from './use-webhooks-source';
import { AccountSetting, useAccountSetting } from '@/services/auth/account-settings';
import { useProductTaxonomy } from '@/services/product-taxonomy';
import { PlumaModal as PlumaModalCss } from '@customerio/pluma-components/css/components';
import type { useAnalytics } from '@/services/analytics';
import { sentryCaptureException } from '@/utils/sentry';
import { isEmptyString } from '@/utils/string';
import { useHotkey, useKeyHold } from '@tanstack/react-hotkeys';
import { QuickLookPanel } from './quick-look';
import { SearchResultItem } from './search-result-item';
import { LocalCommandsSection } from './local-commands-section';
import clsx from 'clsx';
import { themeVars } from '@customerio/pluma-components/css/theme-tokens';
import { CommandPrimerItem } from './command-primer-item';
import { FilterCategoryCommandGroup } from './filter-category-command-group';
import { getAvailableCategoryFilterOptions, type CategoryFilterOption } from './utils/category-filter-options';
import { getCommandModeQuery, getFilterModeQuery, isCommandModeInput, isFilterModeInput } from './utils/shortcut-mode';

// Pluma's ModalPrimitive prop is in milliseconds; our token is in seconds.
const DIALOG_ANIMATION_DURATION_MS = Math.round(FAST_02_DURATION * 1000);
const LOADING_SKELETON_COUNT: number[] = Array(8).fill(0);

type CustomersListQuery = NonNullable<
  paths['/v1/environments/{environment_id}/customers']['get']['parameters']['query']
>;

type CommandDialogData = {
  session: ReturnType<typeof useCopilotSessionContext>;
  workspace: ReturnType<typeof useCurrentWorkspace>;
  initialInputValue?: string;
  initialActiveFilter?: string;
  initialActiveSubFilter?: string | null;
  persistInputValue: (value: string) => void;
  persistActiveFilter: (value: string) => void;
  persistActiveSubFilter: (value: string | null) => void;
  track: ReturnType<typeof useAnalytics>['track'];
  openCopilot: ReturnType<typeof useCopilotSidebar>['openCopilot'];
  colorScheme: ReturnType<typeof useThemeContext>['colorScheme'];
  setColorScheme: ReturnType<typeof useThemeContext>['setColorScheme'];
  canToggleTheme: ReturnType<typeof useThemeContext>['canToggleTheme'];
  sideNavState: ReturnType<typeof useSideNavState>;
  toggleSideNav: ReturnType<typeof useToggleSideNav>;
  isMasqWithoutAccess: boolean;
  isBuilder: boolean;
  guard: PermissionGuard | null;
};

export function CommandDialog({ data, onClose, modalState }: PlumaManagedModalProps<CommandDialogData>) {
  const { isOpen: isDialogOpen, onClose: onModalManagerClose, onCloseComplete, modalContext } = modalState;
  const isAiEnabled = useAccountSetting(AccountSetting.AiEnabled);
  const taxonomy = useProductTaxonomy();
  const isAgentFullscreen = useIsAgentFullscreen();
  const showAskAssistant = isAiEnabled && !isAgentFullscreen;
  const safeNavigate = useSafeNavigate();
  const [inputValue, setInputValue] = useState(data.initialInputValue ?? '');
  const trimmedInputValue = inputValue.trim();
  const [debouncedInputValue] = useDebouncedValue(trimmedInputValue, 250);
  const isCommandMode = isCommandModeInput(inputValue);
  const isFilterMode = isFilterModeInput(inputValue);
  const isShortcutMode = isCommandMode || isFilterMode;
  const commandModeQuery = isCommandMode ? getCommandModeQuery(inputValue) : '';
  const filterModeQuery = isFilterMode ? getFilterModeQuery(inputValue) : '';
  const hasStaleShortcutModeDebouncedValue =
    !isShortcutMode && (isCommandModeInput(debouncedInputValue) || isFilterModeInput(debouncedInputValue));
  const searchInputValue =
    isShortcutMode || trimmedInputValue.length === 0 || hasStaleShortcutModeDebouncedValue ? '' : debouncedInputValue;
  const isSearchInputDebouncing =
    !isShortcutMode && trimmedInputValue.length > 0 && searchInputValue !== trimmedInputValue;
  const localCommandQuery = isCommandMode ? commandModeQuery : isFilterMode ? '' : inputValue;

  const [selectedValue, setSelectedValue] = useState('');

  const isMetaHeld = useKeyHold('Meta');
  const isCtrlHeld = useKeyHold('Control');

  const {
    session,
    workspace,
    persistInputValue,
    persistActiveFilter,
    persistActiveSubFilter,
    track,
    openCopilot,
    isMasqWithoutAccess,
    isBuilder,
    guard,
  } = data;
  const workspaceId = workspace?.id ?? 0;

  const handleInputFocus = useCallback((event: Event & { target: HTMLInputElement }) => {
    event.target.select();
  }, []);

  const handleFiltersKeyDown = useCallback((event: KeyboardEvent<HTMLElement>) => {
    if (event.key === 'Escape') {
      const hasOpenDropdown = event.currentTarget.querySelector('[aria-expanded="true"]') != null;
      if (hasOpenDropdown) {
        event.stopPropagation();
      }
      return;
    }
    event.stopPropagation();
  }, []);

  const queryStartTimeRef = useRef<number | null>(null);
  const previousDebouncedInputRef = useRef(debouncedInputValue);
  const userHasNavigatedRef = useRef(false);

  const openTimeRef = useRef(Date.now());
  const previousIsDialogOpenRef = useRef(isDialogOpen);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const dialogRef = useRef<HTMLDivElement | null>(null);

  // used for tracking the time the user started typing to get the time to first result
  if (debouncedInputValue !== previousDebouncedInputRef.current) {
    previousDebouncedInputRef.current = debouncedInputValue;
    if (debouncedInputValue.length > 0) {
      queryStartTimeRef.current = Date.now();
    }
    userHasNavigatedRef.current = false;
  }
  // used for tracking the time the dialog was opened to get the time open
  if (isDialogOpen !== previousIsDialogOpenRef.current) {
    previousIsDialogOpenRef.current = isDialogOpen;
    if (isDialogOpen === true) {
      openTimeRef.current = Date.now();
    }
  }

  const isSearchingEnabled = isDialogOpen === true && searchInputValue.length > 0 && workspaceId > 0;

  const canAccessForms = !isBuilder && guard?.hasPermission('journeys/imports/view') === true;
  const canAccessJourneyIntegrations =
    !isBuilder && guard?.hasAnyPermission('journeys/imports/view', 'journeys/exports/view') === true;
  // Object type discovery needs both permissions: object_types/view covers the type metadata,
  // while objects/view covers the preview row fetch and destination object list page.
  const canAccessObjectTypes =
    !isBuilder &&
    guard?.hasPermission('journeys/object_types/view') === true &&
    guard.hasPermission('journeys/objects/view') === true;

  // Deferred sources (docs, integrations, forms) fire after scroll or a keyboard accessibility fallback timeout.
  // Reset on each new query so deferred sources don't carry over to a fresh search.
  // Adjusted during render (not in an effect) to avoid the extra paint cycle.
  const [deferredEnabled, setDeferredEnabled] = useState(false);
  const [lastDebouncedInputValue, setLastDebouncedInputValue] = useState(debouncedInputValue);
  const sentinelRef = useRef<HTMLDivElement>(null);

  if (lastDebouncedInputValue !== debouncedInputValue) {
    setLastDebouncedInputValue(debouncedInputValue);
    setDeferredEnabled(false);
  }

  const [activeCategory, setActiveCategory] = useState<ActiveCategory>(() => {
    const stored = data.initialActiveFilter;
    if (isMasqWithoutAccess && stored === 'people') return 'all';
    if (isBuilder && (stored === SEARCH_KIND_FILTERS.campaigns || stored === SEARCH_KIND_FILTERS.actions)) return 'all';
    if (!canAccessForms && stored === 'forms') return 'all';
    if (!canAccessObjectTypes && stored === 'objects') return 'all';
    const objectTypeCategories: ActiveCategory[] = canAccessObjectTypes ? ['objects'] : [];
    const validCategories: ActiveCategory[] = [
      'all',
      'people',
      'docs',
      'integrations',
      'forms',
      ...objectTypeCategories,
      ...Object.values(SEARCH_KIND_FILTERS),
    ];
    return isActiveCategory(stored, validCategories) ? stored : 'all';
  });

  const [activeTemplateType, setActiveTemplateType] = useState<TemplateType | null>(() => {
    if (data.initialActiveFilter !== SEARCH_KIND_FILTERS.templates) return null;
    const stored = data.initialActiveSubFilter;
    const validTemplateTypes = TEMPLATE_TYPE_FILTER_OPTIONS.map((o) => o.value as string);
    return stored != null && validTemplateTypes.includes(stored) ? (stored as TemplateType) : null;
  });

  const activeKindFilter = isSearchKindFilter(activeCategory) ? activeCategory : null;

  const activeSources = useMemo<ActiveSources>(
    () =>
      buildActiveSources({
        activeCategory,
        activeKindFilter,
        canAccessForms,
        canAccessJourneyIntegrations,
        canAccessObjectTypes,
      }),
    [activeCategory, activeKindFilter, canAccessForms, canAccessJourneyIntegrations, canAccessObjectTypes],
  );

  const encodedFilters = useMemo(() => {
    if (activeKindFilter != null) {
      if (activeKindFilter === SEARCH_KIND_FILTERS.templates && activeTemplateType != null) {
        return encodeSearchFilters({ kind: [activeKindFilter], template_type: [activeTemplateType] });
      }
      return encodeSearchFilters({ kind: [activeKindFilter] });
    }
    return undefined;
  }, [activeKindFilter, activeTemplateType]);

  const peopleSearchTerm = useMemo(
    () => (activeSources.people ? buildPeopleSearchQuery(searchInputValue) : null),
    [activeSources.people, searchInputValue],
  );

  // Exact id lookup fired alongside the email search
  const peopleIdFilter = buildPeopleIdFilter(searchInputValue);

  const isDocsSearchingEnabled = isDialogOpen === true && searchInputValue.length > 0;
  const sourceEnabled = useMemo(
    () =>
      buildSourceEnabledStates({
        activeSources,
        activeCategory,
        isSearchingEnabled,
        isDocsSearchingEnabled,
        deferredEnabled,
      }),
    [activeSources, activeCategory, isSearchingEnabled, isDocsSearchingEnabled, deferredEnabled],
  );

  const [searchQuery, docsQuery, peopleQuery, navigationQuery] = useQueries({
    queries: [
      getHydraUIAPIQueryOptions({
        queryKey: ['search', { params: { workspaceId }, query: { q: searchInputValue, filters: encodedFilters } }],
        queryFn: ({ signal }) =>
          hydraUiApi.search.list({
            params: {
              path: { environment_id: String(workspaceId) },
              query: { q: searchInputValue, filters: encodedFilters },
            },
            signal,
          }),
        queryConfig: {
          enabled: sourceEnabled.search,
          ...COMMAND_DIALOG_QUERY_DEFAULTS,
          select: (data) => ({
            ...data,
            // The generated universal-search result type widens `kind`/`url`/`other` to
            // nullable strings; narrow back to the app's SearchResult union at this boundary.
            results: remapUnsetTemplates(
              ((data.results ?? []) as unknown as SearchResult[]).filter((r) => r.kind !== 'navigation'),
            ),
          }),
        },
      }),
      {
        ...docsSearchOptions(searchInputValue),
        enabled: sourceEnabled.docs,
      },
      getHydraUIAPIQueryOptions({
        queryKey: [
          'getPeople',
          {
            params: { workspaceId },
            query: { email: peopleSearchTerm ?? undefined, asc: false, page: 1 },
            idFilter: peopleIdFilter,
          },
        ],
        queryFn: async ({ signal }) => {
          const listCustomers = (query: CustomersListQuery) =>
            hydraUiApi.customers.list({ params: { path: { environment_id: String(workspaceId) }, query }, signal });

          // Email (name/email) search + best-effort exact id lookup, settled independently.
          const [emailMatches, idMatches] = (
            await Promise.allSettled([
              listCustomers({ email: peopleSearchTerm ?? undefined, asc: false, page: 1 }),
              listCustomers({ filters: peopleIdFilter }),
            ])
          ).map((res) => (res.status === 'fulfilled' ? (res.value.customers ?? []) : []));

          const idMatch = idMatches[0]; // exact-id search yields at-most 1 result
          return {
            customers: mergePeopleResults(emailMatches, idMatch),
            idMatch,
          };
        },
        queryConfig: {
          enabled: sourceEnabled.people && peopleSearchTerm != null && !isMasqWithoutAccess,
          ...COMMAND_DIALOG_QUERY_DEFAULTS,
          // Endpoint returns up to 50; slice to 10 like other sources. The exact-id match is at the
          // front (mergePeopleResults), so it survives the slice.
          select: (data): ScoredResult[] =>
            data.customers.slice(0, 10).map((customer) => {
              const result = mapCustomerToSearchResult(customer, workspaceId, taxonomy.term('people.titleSingular'));
              // Score the exact id match at 1 so it leads the results.
              const isExactIdMatch = data.idMatch != null && getCustomerKey(data.idMatch) === getCustomerKey(customer);
              const clientScore = isExactIdMatch ? 1 : scoreCustomerResult(result, searchInputValue);
              return { ...result, clientScore };
            }),
        },
      }),
      {
        ...navigationSearchOptions(searchInputValue, workspaceId, guard, { taxonomy }),
        enabled: sourceEnabled.navigation,
      },
    ],
  });

  const adAudiencesSource = useAdAudiencesSource({
    workspaceId,
    enabled: sourceEnabled.adAudiences,
    query: searchInputValue,
  });

  const integrationsSource = useIntegrationsSource({
    workspaceId,
    enabled: sourceEnabled.integrations,
    query: searchInputValue,
  });

  const formsSource = useFormsSource({
    workspaceId,
    enabled: sourceEnabled.forms,
    query: searchInputValue,
  });

  const webhooksSource = useWebhooksSource({
    workspaceId,
    enabled: sourceEnabled.webhooks,
    query: searchInputValue,
  });

  const cdpOutSource = useCdpOutSource({
    workspaceId,
    enabled: sourceEnabled.cdpOut,
    query: searchInputValue,
  });

  const dataOutSource = useDataOutSource({
    workspaceId,
    enabled: sourceEnabled.dataOut,
    query: searchInputValue,
  });

  const objectTypesSource = useObjectTypesSource({
    workspaceId,
    enabled: sourceEnabled.objectTypes,
    query: searchInputValue,
  });

  const sources = {
    search: searchQuery,
    people: peopleQuery,
    docs: docsQuery,
    navigation: navigationQuery,
    integrations: integrationsSource,
    adAudiences: adAudiencesSource,
    forms: formsSource,
    webhooks: webhooksSource,
    cdpOut: cdpOutSource,
    dataOut: dataOutSource,
    objectTypes: objectTypesSource,
  } satisfies Record<SourceKey, SourceQueryState>;

  const activeSourceStates = SOURCE_KEYS.filter((key) => activeSources[key]).map((key) => sources[key]);

  // Deferred sources excluded in 'all' mode — they load after scroll/fallback and must
  // not trigger the loading spinner or clear the quick look preview for eager results.
  // In their dedicated filter modes they are the primary source and contribute normally.
  const eagerSourceStates = SOURCE_KEYS.filter(
    (key) => activeSources[key] && !(activeCategory === 'all' && DEFERRED_SOURCE_KEYS.has(key)),
  ).map((key) => sources[key]);

  const isError = activeSourceStates.some((s) => s.isError);
  const isLoading = eagerSourceStates.some((s) => s.isLoading);
  const isQuickLookSourcesFetching = isSearchingEnabled && eagerSourceStates.some((s) => s.isFetching);
  const dataUpdatedAt = Math.max(0, ...activeSourceStates.map((s) => s.dataUpdatedAt));
  const hasDeferredActiveSource = hasAnyDeferredSource(activeSources);

  useEffect(() => {
    if (deferredEnabled || activeCategory !== 'all' || !isSearchingEnabled || isLoading) return;
    if (!hasDeferredActiveSource) return;

    const timeoutId = window.setTimeout(() => {
      setDeferredEnabled(true);
    }, DEFERRED_SOURCE_FALLBACK_DELAY_MS);

    return () => window.clearTimeout(timeoutId);
  }, [activeCategory, searchInputValue, deferredEnabled, hasDeferredActiveSource, isLoading, isSearchingEnabled]);

  useEffect(() => {
    listRef.current?.scrollTo({ top: 0 });
  }, [activeCategory, activeTemplateType]);

  const closeDialog = useCallback(() => {
    onClose();
  }, [onClose]);

  const allApiResults = useMemo(
    () =>
      buildAllApiResults(activeSources, {
        search: searchQuery.data?.results ?? [],
        people: peopleQuery.data ?? [],
        docs: docsQuery.data ?? [],
        navigation: navigationQuery.data ?? [],
        integrations: integrationsSource.results,
        adAudiences: adAudiencesSource.results,
        forms: formsSource.results,
        webhooks: webhooksSource.results,
        cdpOut: cdpOutSource.results,
        dataOut: dataOutSource.results,
        objectTypes: objectTypesSource.results,
      }),
    [
      activeSources,
      searchQuery.data?.results,
      peopleQuery.data,
      docsQuery.data,
      navigationQuery.data,
      integrationsSource.results,
      adAudiencesSource.results,
      formsSource.results,
      webhooksSource.results,
      cdpOutSource.results,
      dataOutSource.results,
      objectTypesSource.results,
    ],
  );

  // Builder filter strips campaign/action because those arrive via the shared searchQuery
  // (which can't be disabled per-kind). custom_form results are already absent for builders:
  // canAccessForms sets activeSources.forms=false, disabling the dedicated queries entirely.
  const filteredApiResults = useMemo(() => {
    if (isShortcutMode) return [];
    return isBuilder ? allApiResults.filter((r) => r.kind !== 'campaign' && r.kind !== 'action') : allApiResults;
  }, [allApiResults, isBuilder, isShortcutMode]);

  const groupedResults = useMemo(() => groupSearchResults(filteredApiResults), [filteredApiResults]);

  const resultsByKey = useMemo(() => {
    const map = new Map<string, SearchResult>();
    for (const [, items] of groupedResults) {
      for (let index = 0; index < items.length; index++) {
        const item = items[index];
        const key = `${item.kind}-${index}-${item.doc_id ?? encodeURIComponent(item.url)}`;
        map.set(key, item);
      }
    }
    return map;
  }, [groupedResults]);

  const selectedResult = resultsByKey.get(selectedValue) ?? null;

  // When a new group arrives sorted before the currently auto-selected group (e.g. docs resolves
  // after forms and renders above it), reset selection so cmdk re-picks the first item.
  // Skipped if the user has manually navigated via arrow keys to avoid disrupting their intent.
  const selectedGroupIndex =
    selectedResult != null ? groupedResults.findIndex(([kind]) => kind === selectedResult.kind) : -1;
  if (selectedGroupIndex > 0 && !userHasNavigatedRef.current) {
    setSelectedValue('');
  }

  const handleValueChange = useCallback((value: string) => {
    setSelectedValue(value);
  }, []);

  const isQuickLookOpen =
    selectedResult != null && (SEARCH_RESULT_KIND_METADATA[selectedResult.kind]?.hasQuickLookPreview ?? false);

  const availableCategoryFilterOptions = useMemo(
    () =>
      getAvailableCategoryFilterOptions({
        canAccessForms,
        canAccessObjectTypes,
        isBuilder,
        isMasqWithoutAccess,
        taxonomy,
      }),
    [canAccessForms, canAccessObjectTypes, isBuilder, isMasqWithoutAccess, taxonomy],
  );

  const commandDialogFilters = useMemo(
    () => [
      {
        key: 'category',
        label: 'Category',
        type: 'radio' as const,
        isAlwaysShown: true,
        options: availableCategoryFilterOptions
          .filter((option) => option.value !== 'all')
          .map(({ label, value }) => ({ label, value })),
      },
      ...(activeCategory === SEARCH_KIND_FILTERS.templates
        ? [
            {
              key: 'type',
              label: 'Type',
              type: 'radio' as const,
              options: [...TEMPLATE_TYPE_FILTER_OPTIONS],
            },
          ]
        : []),
    ],
    [activeCategory, availableCategoryFilterOptions],
  );

  const filterValues = useMemo(() => {
    const values: { key: string; value: string }[] = [];
    if (activeCategory !== 'all') values.push({ key: 'category', value: activeCategory });
    if (activeTemplateType != null) values.push({ key: 'type', value: activeTemplateType });
    return values;
  }, [activeCategory, activeTemplateType]);

  const categoryFilterOptions = useMemo<CategoryFilterOption[]>(() => {
    const [allOption, ...options] = availableCategoryFilterOptions;
    return [allOption, ...options.sort((a, b) => a.label.localeCompare(b.label))];
  }, [availableCategoryFilterOptions]);

  const filteredCategoryOptions = useMemo(() => {
    const query = filterModeQuery.toLowerCase();
    if (query.length === 0) return categoryFilterOptions;
    return categoryFilterOptions.filter(
      (option) => option.label.toLowerCase().includes(query) || option.value.toLowerCase().includes(query),
    );
  }, [categoryFilterOptions, filterModeQuery]);

  const handleFiltersChange = useCallback(
    (key: string, value: string) => {
      void track('universalSearch', 'filterChanged', {
        'Filter Key': key,
        'Filter Value': value,
        'Previous Filter': activeCategory,
        'Previous Sub Filter': activeTemplateType ?? 'none',
        Query: debouncedInputValue,
      });
      if (key === 'category') {
        setActiveCategory(value as ActiveCategory);
        persistActiveFilter(value);
        if (value !== SEARCH_KIND_FILTERS.templates) {
          setActiveTemplateType(null);
          persistActiveSubFilter(null);
        }
      } else if (key === 'type') {
        setActiveTemplateType(value as TemplateType);
        persistActiveSubFilter(value);
      }
    },
    [persistActiveFilter, persistActiveSubFilter, track, activeCategory, activeTemplateType, debouncedInputValue],
  );

  const handleFiltersClear = useCallback(
    (key: string) => {
      void track('universalSearch', 'filterChanged', {
        'Filter Key': key,
        'Filter Value': null,
        'Previous Filter': activeCategory,
        'Previous Sub Filter': activeTemplateType ?? 'none',
        Query: debouncedInputValue,
      });
      if (key === 'type') {
        setActiveTemplateType(null);
        persistActiveSubFilter(null);
      } else {
        setActiveCategory('all');
        persistActiveFilter('all');
        setActiveTemplateType(null);
        persistActiveSubFilter(null);
      }
    },
    [persistActiveFilter, persistActiveSubFilter, track, activeCategory, activeTemplateType, debouncedInputValue],
  );

  const handleFiltersClearAll = useCallback(() => {
    void track('universalSearch', 'filterChanged', {
      'Filter Key': 'category',
      'Filter Value': 'all',
      'Previous Filter': activeCategory,
      'Previous Sub Filter': activeTemplateType ?? 'none',
      Query: debouncedInputValue,
    });
    setActiveCategory('all');
    persistActiveFilter('all');
    setActiveTemplateType(null);
    persistActiveSubFilter(null);
  }, [persistActiveFilter, persistActiveSubFilter, track, activeCategory, activeTemplateType, debouncedInputValue]);

  const handleInputChange = useCallback(
    (value: string) => {
      setInputValue(value);
      persistInputValue(value);
    },
    [persistInputValue],
  );

  const openCommandMode = useCallback(
    (query = '') => {
      const trimmedQuery = query.trim();
      handleInputChange(trimmedQuery.length > 0 ? `>${trimmedQuery}` : '>');
      window.requestAnimationFrame(() => inputRef.current?.focus());
    },
    [handleInputChange],
  );

  const openFilterMode = useCallback(() => {
    handleInputChange('/');
    window.requestAnimationFrame(() => inputRef.current?.focus());
  }, [handleInputChange]);

  const commitCategoryFilter = useCallback(
    (value: ActiveCategory) => {
      handleFiltersChange('category', value);
      handleInputChange('');
      window.requestAnimationFrame(() => inputRef.current?.focus());
    },
    [handleFiltersChange, handleInputChange],
  );

  useHotkey(
    'Backspace',
    (event) => {
      const input = inputRef.current;
      if (input == null) return;
      if (input.value === '>' || input.value === '> ' || input.value === '/' || input.value === '/ ') {
        event.preventDefault();
        event.stopPropagation();
        handleInputChange('');
        return;
      }

      if (input.value.length === 0 && activeCategory !== 'all') {
        event.preventDefault();
        event.stopPropagation();
        commitCategoryFilter('all');
      }
    },
    {
      ignoreInputs: false,
      target: inputRef,
    },
  );

  // Using localCommandQuery instead of debouncedInputValue as we aren't calling
  // an expensive API like /search and there probably won't be many commands to filter by
  const hydraCommands = useStoredHydraCommands(localCommandQuery);
  const campaignRoutingCommands = useCampaignRoutingCommands(localCommandQuery, {
    workspaceId,
    guard,
    navigate: safeNavigate,
  });
  const segmentRoutingCommands = useSegmentRoutingCommands(localCommandQuery, {
    workspaceId,
    guard,
    navigate: safeNavigate,
  });
  const settingsCommands = useSettingsCommands(
    localCommandQuery,
    {
      colorScheme: data.colorScheme,
      setColorScheme: data.setColorScheme,
      canToggleTheme: data.canToggleTheme,
      sideNavState: data.sideNavState,
      toggleSideNav: data.toggleSideNav,
      openCopilot: data.openCopilot,
    },
    { includeEmptyQuery: isCommandMode },
  );

  // Local search results — handlers are called on-demand with the debounced query
  const localSearchGroups = useLocalSearchResults(isShortcutMode ? '' : searchInputValue);

  const apiResultsCount = filteredApiResults.length;
  const localSearchCount = localSearchGroups.reduce((sum, g) => sum + g.results.length, 0);
  const hasNoApiResults = filteredApiResults.length === 0;
  const hasApiResults = filteredApiResults.length > 0;
  const isLocalResultsEnabled = !isFilterMode && (activeCategory === 'all' || isCommandMode);
  const localCommandGroups = useMemo<LocalCommandResultGroup[]>(
    () => [
      {
        id: 'campaign-routing',
        heading: 'Campaign shortcuts',
        placement: 'before-results',
        commands: campaignRoutingCommands,
      },
      {
        id: 'segment-routing',
        heading: 'Segment shortcuts',
        placement: 'before-results',
        commands: segmentRoutingCommands,
      },
      {
        id: 'settings',
        heading: 'Settings',
        placement: 'before-results',
        commands: settingsCommands,
      },
      {
        id: 'quick-actions',
        heading: 'Quick actions',
        placement: 'before-results',
        commands: hydraCommands,
      },
    ],
    [campaignRoutingCommands, hydraCommands, segmentRoutingCommands, settingsCommands],
  );
  const localCommandResultPlan = useMemo(
    () =>
      buildLocalCommandResultPlan({
        commandGroups: localCommandGroups,
        localSearchCount,
        apiResultsCount,
        hasApiResults,
        isLocalResultsEnabled,
      }),
    [apiResultsCount, hasApiResults, isLocalResultsEnabled, localCommandGroups, localSearchCount],
  );

  // useLayoutEffect guarantees both listRef.current and sentinelRef.current are set before
  // the observer is created — callback refs fire bottom-up during commit, so a callback ref
  // on the sentinel would race with listRef.current on the parent Command.List.
  useLayoutEffect(() => {
    if (deferredEnabled) return;
    const sentinel = sentinelRef.current;
    const list = listRef.current;
    if (sentinel == null || list == null) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting === true) {
          setDeferredEnabled(true);
          observer.disconnect();
        }
      },
      { root: list, threshold: 0 },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [deferredEnabled, hasApiResults, searchInputValue]);
  const hasLocalCommands = localCommandResultPlan.localCommandCount > 0;
  const hasLocalSearchResults = localCommandResultPlan.localSearchCount > 0;
  const shouldShowEmptyCommandPrimers = trimmedInputValue.length === 0 && activeCategory === 'all';
  const shouldShowTypedSeeMoreActions =
    !isShortcutMode && activeCategory === 'all' && trimmedInputValue.length > 0 && hasLocalCommands;
  const hasNoResults = hasNoApiResults && !hasLocalCommands && !hasLocalSearchResults;
  // True while eager sources finished with 0 results but deferred sources (integrations, docs, etc.)
  // haven't been enabled yet — the 1500ms fallback timeout is still running.
  const isDeferredPending =
    !deferredEnabled && hasDeferredActiveSource && activeCategory === 'all' && isSearchingEnabled;
  /** Mirrors Command.Empty rendering "No results found for …" — quick look should show No preview, not a stale selection */
  const universalSearchShowsNoResultsEmptyState =
    !isShortcutMode &&
    searchInputValue.length > 0 &&
    !isSearchInputDebouncing &&
    !isLoading &&
    !isDeferredPending &&
    (hasNoResults || (isError && filteredApiResults.length === 0));
  const commandModeShowsNoResults = isCommandMode && !isLoading && !hasLocalCommands;
  const askAssistantVisible =
    !isShortcutMode &&
    showAskAssistant &&
    searchInputValue.length > 0 &&
    !isSearchInputDebouncing &&
    !isLoading &&
    !hasNoResults &&
    !searchQuery.isError;
  const totalVisibleCount = localCommandResultPlan.totalVisibleCount + (askAssistantVisible ? 1 : 0);
  const askAssistantPosition = askAssistantVisible ? totalVisibleCount : null;
  const categorySearchCopy = getCategorySearchCopy(activeCategory, taxonomy);
  const emptyStateTitle = activeCategory !== 'all' ? categorySearchCopy.placeholder : 'Search this workspace';

  const timeToFirstResultMs =
    queryStartTimeRef.current != null ? Math.max(0, dataUpdatedAt - queryStartTimeRef.current) : 0;

  const handleItemSelect = useCallback(
    (url: string, kind: SearchResult['kind'], position: number) => {
      const isExternal = kind === 'docs';
      const openInNewTab = isExternal || isMetaHeld || isCtrlHeld;

      void track('universalSearch', 'itemSelected', {
        'Item Type': 'search_result',
        'Result Kind': kind,
        'Open in New Tab': openInNewTab,
        Query: debouncedInputValue,
        'Result Position': position,
        'Results Count': totalVisibleCount,
        'API Results Count': apiResultsCount,
        'Time to First Result Ms': timeToFirstResultMs,
        'Active Filter': activeCategory,
        'Active Sub Filter': activeTemplateType ?? 'none',
        'Filters Open': true,
        'Preview Open': isQuickLookOpen,
      });
      if (openInNewTab) {
        window.open(url, '_blank', 'noopener,noreferrer');
        if (!isMetaHeld && !isCtrlHeld) {
          closeDialog();
        }
      } else {
        safeNavigate(url);
        closeDialog();
      }
    },
    [
      safeNavigate,
      closeDialog,
      track,
      isMetaHeld,
      isCtrlHeld,
      debouncedInputValue,
      dataUpdatedAt,
      totalVisibleCount,
      apiResultsCount,
      timeToFirstResultMs,
      activeCategory,
      activeTemplateType,
      isQuickLookOpen,
    ],
  );

  const handleLocalCommandSelect = useCallback(
    (onSelect: () => void | Promise<void>, position: number) => {
      void track('universalSearch', 'itemSelected', {
        'Item Type': 'local_command',
        'Result Kind': 'command',
        Query: debouncedInputValue,
        'Result Position': position,
        'Results Count': totalVisibleCount,
        'API Results Count': apiResultsCount,
        'Open in New Tab': false,
        'Time to First Result Ms': timeToFirstResultMs,
        'Active Filter': activeCategory,
        'Active Sub Filter': activeTemplateType ?? 'none',
        'Filters Open': true,
        'Preview Open': isQuickLookOpen,
      });
      void Promise.resolve(onSelect());
      closeDialog();
    },
    [
      closeDialog,
      track,
      debouncedInputValue,
      totalVisibleCount,
      apiResultsCount,
      timeToFirstResultMs,
      activeCategory,
      activeTemplateType,
      isQuickLookOpen,
    ],
  );

  const handleLocalSearchSelect = useCallback(
    (onSelect: () => void | Promise<void>, position: number) => {
      void track('universalSearch', 'itemSelected', {
        'Item Type': 'local_search',
        'Result Kind': 'search',
        Query: debouncedInputValue,
        'Result Position': position,
        'Results Count': totalVisibleCount,
        'API Results Count': apiResultsCount,
        'Open in New Tab': false,
        'Time to First Result Ms': timeToFirstResultMs,
        'Active Filter': activeCategory,
        'Active Sub Filter': activeTemplateType ?? 'none',
        'Filters Open': true,
        'Preview Open': isQuickLookOpen,
      });
      void Promise.resolve(onSelect());
      closeDialog();
    },
    [
      closeDialog,
      track,
      debouncedInputValue,
      totalVisibleCount,
      apiResultsCount,
      timeToFirstResultMs,
      activeCategory,
      activeTemplateType,
      isQuickLookOpen,
    ],
  );

  const handleAskAssistant = useCallback(
    (resultPosition: number) => {
      void track('universalSearch', 'itemSelected', {
        'Item Type': 'ask_assistant',
        'Result Kind': 'assistant',
        Query: debouncedInputValue,
        'Result Position': resultPosition,
        'Results Count': totalVisibleCount,
        'API Results Count': apiResultsCount,
        'Open in New Tab': false,
        'Time to First Result Ms': timeToFirstResultMs,
        'Active Filter': activeCategory,
        'Active Sub Filter': activeTemplateType ?? 'none',
        'Filters Open': true,
        'Preview Open': isQuickLookOpen,
      });
      openCopilot({ triggerSource: 'command_dialog' });
      if (debouncedInputValue.length > 0) {
        void session.sendMessage(debouncedInputValue).catch(sentryCaptureException);
      }
      closeDialog();
    },
    [
      closeDialog,
      track,
      debouncedInputValue,
      session,
      openCopilot,
      totalVisibleCount,
      apiResultsCount,
      timeToFirstResultMs,
      activeCategory,
      activeTemplateType,
      isQuickLookOpen,
    ],
  );

  const handleDialogClose = useCallback<NonNullable<ModalPrimitiveProps['onClose']>>(
    (_event, reason) => {
      void track('universalSearch', 'closed', {
        Reason: reason ?? 'dismiss',
        'Had Query': !isEmptyString(inputValue.trim()),
        Query: inputValue.trim(),
        'Time Open Ms': Date.now() - openTimeRef.current,
        'Active Filter': activeCategory,
        'Active Sub Filter': activeTemplateType ?? 'none',
      });
      onModalManagerClose?.(_event, reason);
    },
    [track, onModalManagerClose, inputValue, activeCategory, activeTemplateType],
  );

  return (
    <ModalPrimitive
      initialFocusIndex={-1}
      animationTransitionDuration={DIALOG_ANIMATION_DURATION_MS}
      isOpen={isDialogOpen ?? false}
      onClose={handleDialogClose}
      onCloseComplete={onCloseComplete}
      modalContext={modalContext}
    >
      <ModalPrimitiveOverlay className={PlumaModalCss.classNames.modalOverlay}>
        <ModalPrimitiveDialog ref={dialogRef} className={clsx(styles.dialog, styles.dialogQuickLookOpen)}>
          <Command
            loop
            label={LABEL}
            shouldFilter={false}
            value={selectedValue}
            onValueChange={handleValueChange}
            onKeyDown={(event) => {
              if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
                userHasNavigatedRef.current = true;
              }
            }}
          >
            <Box mx="200" mt="100" style={{ borderBottom: `1px solid ${themeVars.color['border-minimal']}` }}>
              <InlineStack alignBlock="center" alignInline="space-between" gap="050">
                <Command.Input asChild value={inputValue} onValueChange={handleInputChange}>
                  <TextField
                    ref={inputRef}
                    autoFocus={true}
                    placeholder={categorySearchCopy.placeholder}
                    onInput={(event) => handleInputChange(event.target.value)}
                    onFocus={handleInputFocus}
                    ariaLabel={categorySearchCopy.ariaLabel}
                    className={`${styles.largeTextField} fs-mask`}
                    icon="search"
                    iconColor="accent"
                    size="sm"
                    w="full"
                  />
                </Command.Input>
              </InlineStack>
              {!isCommandMode && filterValues.length > 0 ? (
                // oxlint-disable-next-line jsx-a11y/no-static-element-interactions -- keydown interception explained above
                <div className={styles.filtersInner} onKeyDown={handleFiltersKeyDown}>
                  <Box pb="100">
                    <Filters
                      filters={commandDialogFilters}
                      values={filterValues}
                      onChange={handleFiltersChange}
                      onClear={handleFiltersClear}
                      onClearAll={handleFiltersClearAll}
                      addButtonVariant="subtle"
                    >
                      <InlineStack alignBlock="center" gap="100">
                        <FiltersActiveList />
                      </InlineStack>
                    </Filters>
                  </Box>
                </div>
              ) : null}
            </Box>
            <Flex direction="row" overflow="hidden">
              <Box flex={1} minW="0" overflow="hidden">
                <Command.List
                  ref={listRef}
                  onPointerMove={(event) => {
                    if ((event.target as HTMLElement).closest('[cmdk-item]') != null) {
                      userHasNavigatedRef.current = true;
                    }
                  }}
                >
                  {shouldShowEmptyCommandPrimers ? (
                    <Command.Group heading="Commands">
                      <CommandPrimerItem
                        value="filter-by-category"
                        icon="filter"
                        label="Filter by category"
                        description="Narrow results to campaigns, segments, templates, and more"
                        hotkey={FILTER_MODE_HOTKEY}
                        onSelect={openFilterMode}
                      />
                      <CommandPrimerItem
                        value="see-more-actions"
                        icon="command"
                        label="See more actions"
                        description="Create, navigate, and change settings"
                        hotkey={COMMAND_MODE_HOTKEY}
                        onSelect={() => openCommandMode()}
                      />
                    </Command.Group>
                  ) : null}
                  {isFilterMode ? (
                    <FilterCategoryCommandGroup
                      activeCategory={activeCategory}
                      options={filteredCategoryOptions}
                      onSelect={commitCategoryFilter}
                    />
                  ) : null}
                  {localCommandResultPlan.beforeResultGroups.length > 0 ? (
                    <LocalCommandsSection
                      commandGroups={localCommandResultPlan.beforeResultGroups}
                      onSelect={handleLocalCommandSelect}
                    />
                  ) : null}
                  {shouldShowTypedSeeMoreActions ? (
                    <Command.Group heading="Commands">
                      <CommandPrimerItem
                        value="see-more-actions"
                        icon="command"
                        label="See more actions"
                        description={`Show all actions matching "${inputValue.trim()}"`}
                        hotkey={COMMAND_MODE_HOTKEY}
                        onSelect={() => openCommandMode(inputValue)}
                      />
                    </Command.Group>
                  ) : null}
                  {hasLocalSearchResults ? (
                    <>
                      {localSearchGroups.map((group, groupIndex) => {
                        const groupOffset = localSearchGroups
                          .slice(0, groupIndex)
                          .reduce((sum, g) => sum + g.results.length, 0);
                        return (
                          <Command.Group key={`search-${group.heading}`} heading={group.heading}>
                            {group.results.map((result, resultIndex) => (
                              <Command.Item
                                key={`search-${result.id}`}
                                value={`search-${result.id}`}
                                onSelect={() =>
                                  handleLocalSearchSelect(
                                    result.onSelect,
                                    localCommandResultPlan.getLocalSearchPosition({
                                      groupOffset,
                                      itemIndex: resultIndex,
                                    }),
                                  )
                                }
                                asChild
                              >
                                <Box alignItems="flex-start" gap="050" display="flex">
                                  <Icon
                                    mt="025"
                                    size="sm"
                                    sizeVersion="v2"
                                    color="base"
                                    name={(result.icon ?? 'search') as IconName}
                                  />
                                  <Flex direction="column" w="full">
                                    <Text text="product-p" truncate>
                                      {result.label}
                                    </Text>
                                    {result.description != null ? (
                                      <Text size="xxs" color="subtle" truncate>
                                        {result.description}
                                      </Text>
                                    ) : null}
                                  </Flex>
                                </Box>
                              </Command.Item>
                            ))}
                          </Command.Group>
                        );
                      })}
                    </>
                  ) : null}
                  {!hasLocalCommands &&
                  !hasLocalSearchResults &&
                  hasNoApiResults &&
                  (isSearchInputDebouncing || isLoading || isDeferredPending) ? (
                    <Command.Loading>
                      {LOADING_SKELETON_COUNT.map((_, i) => (
                        <Stack key={i} pl="300" pr="125" py="050" pt="100" gap="050" alignBlock="center">
                          {i % 4 === 0 ? <SkeletonText text="product-ps" style={{ width: '14ch' }} /> : null}
                          <Box alignItems="flex-start" gap="050" display="flex">
                            <SkeletonShape>
                              <Icon name="search" size="sm" sizeVersion="v2" color="base" />
                            </SkeletonShape>
                            {/* Skeleton does some margin additions and we do a negative to make it look more inline with the icon Skeleton. */}
                            <Stack w="full" style={{ marginTop: -2 }}>
                              <InlineStack alignInline="space-between">
                                <SkeletonText text="product-p" style={{ width: i % 4 === 0 ? '32ch' : '22ch' }} />
                                {i % 5 === 0 ? (
                                  <SkeletonShape>
                                    <Icon name="search" size="sm" sizeVersion="v2" color="base" />
                                  </SkeletonShape>
                                ) : null}
                              </InlineStack>
                              {i % 3 === 0 ? (
                                <InlineStack gap="050">
                                  <SkeletonText text="product-pxs" style={{ width: '16ch' }} />
                                  <SkeletonText text="product-pxs" style={{ width: '16ch' }} />
                                </InlineStack>
                              ) : (
                                <SkeletonText text="product-pxs" style={{ width: '32ch' }} />
                              )}
                            </Stack>
                          </Box>
                        </Stack>
                      ))}
                    </Command.Loading>
                  ) : null}
                  {trimmedInputValue.length === 0 && !hasLocalCommands && !hasLocalSearchResults ? (
                    <Command.Empty asChild>
                      <EmptyState>
                        <EmptyStateTitle icon="search">{emptyStateTitle}</EmptyStateTitle>
                        <EmptyStateDescription>
                          {activeCategory === 'all' ? (
                            <>
                              Type <KeyboardShortcut hotkey={FILTER_MODE_HOTKEY} /> to filter or{' '}
                              <KeyboardShortcut hotkey={COMMAND_MODE_HOTKEY} /> for actions.
                            </>
                          ) : (
                            <>Start typing to search within this category.</>
                          )}
                        </EmptyStateDescription>
                      </EmptyState>
                    </Command.Empty>
                  ) : null}
                  {commandModeShowsNoResults ? (
                    <Command.Empty asChild>
                      <EmptyState>
                        <EmptyStateTitle icon="command">
                          {commandModeQuery.length > 0 ? (
                            <>
                              No shortcuts found for "<span className="fs-mask">{commandModeQuery}</span>"
                            </>
                          ) : (
                            'No shortcuts available'
                          )}
                        </EmptyStateTitle>
                        <EmptyStateDescription>
                          Try another action or press Backspace to return to search.
                        </EmptyStateDescription>
                      </EmptyState>
                    </Command.Empty>
                  ) : null}
                  {universalSearchShowsNoResultsEmptyState ? (
                    <Command.Empty asChild>
                      <EmptyState>
                        <EmptyStateTitle icon="search">
                          No results found for "<span className="fs-mask">{debouncedInputValue}</span>"
                        </EmptyStateTitle>
                        <EmptyStateDescription>
                          Try a different word, or press <KeyboardShortcut hotkey={FILTER_MODE_HOTKEY} /> to narrow by
                          type.
                        </EmptyStateDescription>
                      </EmptyState>
                    </Command.Empty>
                  ) : null}
                  {hasApiResults ? (
                    <>
                      {groupedResults.map(([kind, items], groupIndex) => {
                        const groupOffset = groupedResults
                          .slice(0, groupIndex)
                          .reduce((sum, [, groupItems]) => sum + groupItems.length, 0);
                        return (
                          <Command.Group key={kind} heading={getResultGroupHeading(kind, taxonomy)}>
                            {items.map((item, index) => {
                              const key = `${item.kind}-${index}-${item.doc_id ?? encodeURIComponent(item.url)}`;
                              return (
                                <SearchResultItem
                                  key={key}
                                  item={item}
                                  itemKey={key}
                                  position={localCommandResultPlan.getApiResultPosition({
                                    groupOffset,
                                    itemIndex: index,
                                  })}
                                  onSelect={handleItemSelect}
                                  catalog={integrationsSource.catalog}
                                />
                              );
                            })}
                          </Command.Group>
                        );
                      })}
                    </>
                  ) : null}
                  {localCommandResultPlan.afterResultGroups.length > 0 ? (
                    <LocalCommandsSection
                      commandGroups={localCommandResultPlan.afterResultGroups}
                      onSelect={handleLocalCommandSelect}
                    />
                  ) : null}
                  {askAssistantVisible ? (
                    <Command.Group heading="Actions">
                      <Command.Item
                        key="ask-assistant"
                        value="ask-assistant"
                        onSelect={() => {
                          if (askAssistantPosition == null) return;
                          handleAskAssistant(askAssistantPosition);
                        }}
                        asChild
                      >
                        <Box alignItems="flex-start" gap="050" display="flex">
                          <Icon mt="025" size="sm" sizeVersion="v2" color="base" name="sparkle" />
                          <Flex direction="column" w="full">
                            <Text text="product-p" truncate w="full">
                              Ask Agent
                            </Text>
                            <Text size="xxs" color="subtle" truncate w="full">
                              Send &ldquo;<span className="fs-mask">{debouncedInputValue}</span>&rdquo; to the AI Agent
                            </Text>
                          </Flex>
                        </Box>
                      </Command.Item>
                    </Command.Group>
                  ) : null}
                  {hasApiResults ? <div ref={sentinelRef} aria-hidden="true" style={{ height: 0 }} /> : null}
                </Command.List>
              </Box>
              <div
                className={clsx(
                  styles.quickLookContainer,
                  isQuickLookOpen === false && styles.quickLookContainerHidden,
                )}
              >
                {isQuickLookOpen ? (
                  <QuickLookPanel
                    isLoading={isQuickLookSourcesFetching && !universalSearchShowsNoResultsEmptyState}
                    result={universalSearchShowsNoResultsEmptyState ? null : selectedResult}
                    integrationCatalog={integrationsSource.catalog}
                  />
                ) : null}
              </div>
            </Flex>
          </Command>
          <InlineStack
            w="full"
            alignInline="space-between"
            py="100"
            px="200"
            alignBlock="center"
            className={styles.footer}
          >
            <Flex gap="050" alignItems="center">
              <KeyboardShortcut hotkey="Escape" />
              <Text size="xxs" color="subtle">
                Close
              </Text>
            </Flex>

            <InlineStack alignInline="space-between" gap="300">
              <Flex gap="050" alignItems="center">
                <KeyboardShortcut hotkey="ArrowUp" />
                <KeyboardShortcut hotkey="ArrowDown" />
                <Text size="xxs" color="subtle">
                  Navigate
                </Text>
              </Flex>
              <Flex gap="050" alignItems="center">
                <KeyboardShortcut hotkey="Enter" />
                <Text size="xxs" color="subtle">
                  Select
                </Text>
              </Flex>
              {!isShortcutMode ? (
                <>
                  <Flex gap="050" alignItems="center">
                    <KeyboardShortcut hotkey={FILTER_MODE_HOTKEY} />
                    <Text size="xxs" color="subtle">
                      Filter
                    </Text>
                  </Flex>
                  <Flex gap="050" alignItems="center">
                    <KeyboardShortcut hotkey={COMMAND_MODE_HOTKEY} />
                    <Text size="xxs" color="subtle">
                      Actions
                    </Text>
                  </Flex>
                </>
              ) : null}
              <Flex gap="050" alignItems="center">
                <KeyboardShortcut hotkey="Mod+Enter" />
                <Text size="xxs" color="subtle">
                  Open in new tab
                </Text>
              </Flex>
            </InlineStack>
          </InlineStack>
        </ModalPrimitiveDialog>
      </ModalPrimitiveOverlay>
    </ModalPrimitive>
  );
}
